import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";

import { clientKeys } from "api/clients";
import { TenantIdentity } from "Components/Tenants";
import { Badge, Button, ConfirmDialog, EmptyState, notify } from "Components/ui";
import { Building2, Trash2, Undo2 } from "Components/ui/icons";
import { formatDateTime } from "utils/format";
import {
  CLIENT_RECYCLE_EVENT,
  RECYCLE_RETENTION_DAYS,
  ClientRecycleBinEntry,
  getClientRecycleBinEntries,
  permanentlyDeleteClientFromRecycleBin,
  restoreClientFromRecycleBin,
} from "utils/recycleBin";

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

const formatRemainingTime = (deletedAt: number) => {
  const msLeft = Math.max(0, deletedAt + RECYCLE_RETENTION_DAYS * DAY_MS - Date.now());
  if (msLeft >= DAY_MS) {
    const days = Math.ceil(msLeft / DAY_MS);
    return `${days} day${days === 1 ? "" : "s"} left`;
  }
  const hours = Math.ceil(msLeft / HOUR_MS);
  return `${hours} hour${hours === 1 ? "" : "s"} left`;
};

/** Settings → Recycle bin: clients deleted in this browser, restorable for a while. */
const RecycleBinView: React.FC = () => {
  const queryClient = useQueryClient();
  const [entries, setEntries] = React.useState<ClientRecycleBinEntry[]>([]);
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = React.useState<ClientRecycleBinEntry | null>(null);

  React.useEffect(() => {
    const refresh = () => setEntries(getClientRecycleBinEntries());
    refresh();
    window.addEventListener(CLIENT_RECYCLE_EVENT, refresh);
    return () => window.removeEventListener(CLIENT_RECYCLE_EVENT, refresh);
  }, []);

  const restore = async (entry: ClientRecycleBinEntry) => {
    setBusyId(entry.binId);
    try {
      const restored = await restoreClientFromRecycleBin(entry.binId);
      if (!restored) {
        notify.error("This client couldn't be restored.");
        return;
      }
      notify.success(`${restored.name} (${restored.slug}) restored to Locked`);
      setEntries(getClientRecycleBinEntries());
      await queryClient.invalidateQueries({ queryKey: clientKeys.all });
    } catch (err) {
      notify.error(err);
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = () => {
    if (!pendingDelete) {
      return;
    }
    if (!permanentlyDeleteClientFromRecycleBin(pendingDelete.binId)) {
      notify.error("This client couldn't be deleted.");
      return;
    }
    notify.success(`${pendingDelete.clientName} permanently deleted`);
    setEntries(getClientRecycleBinEntries());
  };

  return (
    <div className="flex w-full flex-col gap-5">
      <p className="text-sm text-muted-foreground">
        Clients deleted in this browser stay here for {RECYCLE_RETENTION_DAYS} days. A restored
        client comes back as a new, Locked tenant.
      </p>

      <section aria-label="Deleted clients">
        <div className="mb-2 flex items-center justify-between gap-3">
          <h4 className="text-sm font-semibold">Clients</h4>
          <Badge tone="neutral">
            {entries.length} item{entries.length === 1 ? "" : "s"}
          </Badge>
        </div>
        {entries.length === 0 ? (
          <EmptyState compact icon={<Building2 />} title="No deleted clients" />
        ) : (
          <ul className="divide-y divide-border rounded-card border border-border bg-surface px-4">
            {entries.map((entry) => (
              <li
                key={entry.binId}
                className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 space-y-1">
                  <TenantIdentity
                    name={entry.clientName}
                    slug={entry.clientData.slug}
                    uri={entry.clientData.uri}
                    className="text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Deleted {formatDateTime(entry.deletedAt)} ·{" "}
                    {formatRemainingTime(entry.deletedAt)}
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    leftIcon={<Undo2 />}
                    loading={busyId === entry.binId}
                    disabled={busyId !== null}
                    onClick={() => restore(entry)}
                  >
                    Restore
                  </Button>
                  <Button
                    size="sm"
                    variant="danger-ghost"
                    leftIcon={<Trash2 />}
                    disabled={busyId !== null}
                    onClick={() => setPendingDelete(entry)}
                  >
                    Delete now
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title={`Permanently delete ${pendingDelete?.clientName ?? ""}?`}
        description="It leaves the recycle bin for good and can't be restored afterwards."
        confirmLabel="Delete now"
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default RecycleBinView;
